import {
  getIframeAnchorTags,
  getUrlParams,
  onClickMetricsHandler,
  onTriggerNewRelicAdBlocked,
  triggerNewRelic,
  triggerNewRelicTimeLog
} from './helpers';
import { htmlAttributes, newRelicConstants } from '../constants';
import { initHeroImpressionWatchers } from './initImpressionWatchers';
import { adServerClients, bannerTypes } from '../constants/constants';
import { determineAdServer } from './utils';

const bannersWithMigratedImpressionLogic = ['skyscraper', 'standard', 'hero', 'middle', 'ingrid', 'leaderboard'];

function getPubRef(data) {
  const params = getUrlParams(data.banner.bannerURL);
  return decodeURIComponent(params.pub_ref);
}

function getCreativeId(bannerURL) {
  let creativeId = '';

  if (bannerURL && bannerURL.indexOf('cdn.revjet.com') === -1) {
    const anchorElement = document.createElement('a');
    anchorElement.href = bannerURL;
    creativeId = anchorElement.pathname.split('-')[1];
  }

  return creativeId;
}

function getItcParams({ banner = {} }) {

  return {
    campaignId: banner.campaignId ? banner.campaignId : '',
    // banner api maps kevel kevel slotId to placement id
    placementId: banner.placementId ? banner.placementId : '',
    // banner api maps kevel zone id to slot id
    slotId: banner.slotId ? banner.slotId : '',
    creativeId: getCreativeId(banner.bannerURL)
  };
}

function getFrameConfig({ data, requestConfig }) {
  const itcParams = getItcParams(data);
  // returns true if flightId is present
  const isKevel = !!data?.banner?.flightId;

  return {
    thd_itc_suffix: true,
    _tag: data?.banner?.innervateTag,
    _key: data?.banner?.innervateKey,

    ct_url: data?.banner?.onClickBeacon,
    // kevel specific params
    kevel_zone_id: isKevel ? itcParams.slotId : null,
    kevel_site_id: isKevel ? itcParams.placementId : null,
    kevel_campaign_id: isKevel ? itcParams.campaignId : null,
    kevel_flight_id: isKevel ? data?.banner?.flightId : null,
    _opts: {
      embd_tag_id: requestConfig.schemaId,
      autoscale: true,
      // remove b64 once we fully use vantage for creatives

      b64: getPubRef(data)
    }
  };
}

function isNormalBanner({ bannerType = bannerTypes.STANDARD }) {
  return bannerType === bannerTypes.STANDARD;
}

function isSkyscraperBanner(requestConfig) {
  return requestConfig.bannerType === bannerTypes.SKYSCRAPER;
}

function isPIPBanner(requestConfig) {
  return requestConfig.bannerType === bannerTypes.MIDDLE;
}

function isInGridBanner(requestConfig) {
  return requestConfig.bannerType === bannerTypes.INGRID;
}

function isHeroBanner(requestConfig) {
  return requestConfig.bannerType === bannerTypes.HERO;
}

function isBannerWithMigratedImpressionLogic(requestConfig) {
  return bannersWithMigratedImpressionLogic.includes(requestConfig.bannerType)
    || requestConfig.bannerType === undefined;
}

function cleanViewBeacon(viewBeacon) {
  const cleanedViewBeacon = viewBeacon ? viewBeacon.replace('https:', '') : '';
  return cleanedViewBeacon;
}

function addMetrics(bannerDomElement, requestConfig, adServer, invScriptTime, impressionBeaconAddTime) {
  if (isSkyscraperBanner(requestConfig)) {
    triggerNewRelic(newRelicConstants.SKYSCRAPER_BANNER, newRelicConstants.LOAD);
    const skyscraperBannerLoadTime = Date.now();
    // SEND TIME FROM BANNER INITIATED TO AD LOAD
    triggerNewRelicTimeLog(newRelicConstants.SKYSCRAPER_BANNER_AD_LOAD, requestConfig.startTime, skyscraperBannerLoadTime, adServer);
    // SEND TIME FROM INNERVATE SCRIPT INJECTION
    triggerNewRelicTimeLog(newRelicConstants.SKYSCRAPER_BANNER_AD_INNERVATE_SCRIPT_TO_LOAD, invScriptTime,
      skyscraperBannerLoadTime, adServer);

    // SET BANNER INIT TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BANNER_INIT_TIMESTAMP, requestConfig.startTime);
    // SET AD LOAD TIMESTAMP ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_AD_LOAD_TIMESTAMP, skyscraperBannerLoadTime);
    // SET INV SCRIPT INJECT TIME
    bannerDomElement.setAttribute(htmlAttributes.DATA_INV_SCRIPT_TIMESTAMP, invScriptTime);
    // SET BEACON ADD TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BEACON_ADD_TIME, impressionBeaconAddTime);
  }

  if (isPIPBanner(requestConfig)) {
    const pipBannerLoadTime = Date.now();
    triggerNewRelic(newRelicConstants.PIP_BANNER, newRelicConstants.LOAD);
    // SEND TIME FROM BANNER INITIATED TO AD LOAD
    triggerNewRelicTimeLog(newRelicConstants.PIP_BANNER_AD_LOAD, requestConfig.startTime, pipBannerLoadTime, adServer);
    // SEND TIME FROM INNERVATE SCRIPT INJECTION
    triggerNewRelicTimeLog(newRelicConstants.PIP_BANNER_AD_INNERVATE_SCRIPT_TO_LOAD, invScriptTime,
      pipBannerLoadTime, adServer);

    // SET BANNER INIT TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BANNER_INIT_TIMESTAMP, requestConfig.startTime);
    // SET AD LOAD TIMESTAMP ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_AD_LOAD_TIMESTAMP, pipBannerLoadTime);
    // SET INV SCRIPT INJECT TIME
    bannerDomElement.setAttribute(htmlAttributes.DATA_INV_SCRIPT_TIMESTAMP, invScriptTime);
    // SET BEACON ADD TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BEACON_ADD_TIME, impressionBeaconAddTime);
  }

  if (isInGridBanner(requestConfig)) {
    const ingridBannerLoadTime = Date.now();
    triggerNewRelic(newRelicConstants.INGRID_BANNER, newRelicConstants.LOAD);
    // SEND TIME FROM BANNER INITIATED TO AD LOAD
    triggerNewRelicTimeLog(newRelicConstants.INGRID_BANNER_AD_LOAD, requestConfig.startTime, ingridBannerLoadTime, adServer);
    // SEND TIME FROM INNERVATE SCRIPT INJECTION
    triggerNewRelicTimeLog(newRelicConstants.INGRID_BANNER_AD_INNERVATE_SCRIPT_TO_LOAD, invScriptTime,
      ingridBannerLoadTime, adServer);
    // SET BANNER INIT TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BANNER_INIT_TIMESTAMP, requestConfig.startTime);
    // SET AD LOAD TIMESTAMP ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_AD_LOAD_TIMESTAMP, ingridBannerLoadTime);
    // SET INV SCRIPT INJECT TIME
    bannerDomElement.setAttribute(htmlAttributes.DATA_INV_SCRIPT_TIMESTAMP, invScriptTime);
    // SET BEACON ADD TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BEACON_ADD_TIME, impressionBeaconAddTime);
  }

  if (isHeroBanner(requestConfig)) {
    const heroBannerLoadTime = Date.now();
    triggerNewRelic(newRelicConstants.HERO_BANNER, newRelicConstants.LOAD);
    // SEND TIME FROM BANNER INITIATED TO AD LOAD
    triggerNewRelicTimeLog(newRelicConstants.HERO_BANNER_AD_LOAD, requestConfig.startTime, heroBannerLoadTime, adServer);
    // SEND TIME FROM INNERVATE SCRIPT INJECTION
    triggerNewRelicTimeLog(newRelicConstants.HERO_BANNER_AD_INNERVATE_SCRIPT_TO_LOAD, invScriptTime,
      heroBannerLoadTime, adServer);
    // SET BANNER INIT TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BANNER_INIT_TIMESTAMP, requestConfig.startTime);
    // SET AD LOAD TIMESTAMP ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_AD_LOAD_TIMESTAMP, heroBannerLoadTime);
    // SET INV SCRIPT INJECT TIME
    bannerDomElement.setAttribute(htmlAttributes.DATA_INV_SCRIPT_TIMESTAMP, invScriptTime);
    // SET BEACON ADD TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BEACON_ADD_TIME, impressionBeaconAddTime);
  }

  if (isNormalBanner(requestConfig)) {
    window.scrollTo(window.scrollX, window.scrollY - 1);
    window.scrollTo(window.scrollX, window.scrollY + 1);
    const standardBannerLoadTime = Date.now();
    // SEND TIME FROM BANNER INITIATED TO AD LOAD
    triggerNewRelicTimeLog(newRelicConstants.STANDARD_BANNER_AD_LOAD, requestConfig.startTime, standardBannerLoadTime, adServer);
    // SEND TIME FROM INNERVATE SCRIPT INJECTION
    triggerNewRelicTimeLog(newRelicConstants.STANDARD_BANNER_AD_INNERVATE_SCRIPT_TO_LOAD, invScriptTime,
      standardBannerLoadTime, adServer);

    // SET BANNER INIT TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BANNER_INIT_TIMESTAMP, requestConfig.startTime);
    // SET AD LOAD TIMESTAMP ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_AD_LOAD_TIMESTAMP, standardBannerLoadTime);
    // SET INV SCRIPT INJECT TIME
    bannerDomElement.setAttribute(htmlAttributes.DATA_INV_SCRIPT_TIMESTAMP, invScriptTime);
    // SET BEACON ADD TIME ON ELEMENT
    bannerDomElement.setAttribute(htmlAttributes.DATA_BEACON_ADD_TIME, impressionBeaconAddTime);

    if (window.newrelic && !window.__SponsoredBannerLoaded__) {
      // Dont fire more than once
      window.__SponsoredBannerLoaded__ = true;
      triggerNewRelic(newRelicConstants.STANDARD_BANNER, newRelicConstants.LOAD);
    }
  }
}

function frameLoad({ frameConfig, data, requestConfig, invScriptTime }) {
  const bannerDomElement = window.document.getElementById(frameConfig._opts.embd_tag_id);

  if (bannerDomElement) {
    let impressionBeaconAddTime;
    if (isNormalBanner(requestConfig)) {
      try {
        const carousel = bannerDomElement.closest('.carousel');
        const next = carousel.querySelector('.carousel__next');
        // next.click be gone once placeholder is implemented
        /* istanbul ignore next */
        next.click();
      } catch (error) { /* */ }
    }

    if (bannerDomElement.id === 'hp_sponsored__hero') {
      initHeroImpressionWatchers();
    }

    const itcParams = getItcParams(data);

    const adServer = determineAdServer(data);

    // Remove from previous banner
    bannerDomElement.removeAttribute('data-hl-beacon');
    bannerDomElement.setAttribute(htmlAttributes.DATA_CAMPAIGN_ID, itcParams.campaignId);
    bannerDomElement.setAttribute(htmlAttributes.DATA_PLACEMENT_ID, itcParams.placementId);
    bannerDomElement.setAttribute(htmlAttributes.DATA_SLOT_ID, itcParams.slotId);
    bannerDomElement.setAttribute(htmlAttributes.DATA_CREATIVE_ID, itcParams.creativeId);
    bannerDomElement.setAttribute('data-usenewimpressionlogic', true);
    bannerDomElement.setAttribute(htmlAttributes.DATA_ADSERVER, adServer);
    bannerDomElement.setAttribute(htmlAttributes.DATA_FLIGHT_ID, data?.banner?.flightId);
    if (adServer === adServerClients.KEVEL) {
      bannerDomElement.setAttribute(htmlAttributes.DATA_ON_VIEW_CONVERSION_BEACON, data.banner.viewConversionBeacon);
      bannerDomElement.setAttribute(htmlAttributes.DATA_MCVISID, requestConfig.mcvisID);
    }

    let viewBeacon = data.banner.onViewBeacon;
    viewBeacon = cleanViewBeacon(viewBeacon);

    if (isBannerWithMigratedImpressionLogic(requestConfig)) {
      bannerDomElement.setAttribute('onview-beacon-nucleus', viewBeacon);
      impressionBeaconAddTime = Date.now();
    } else {
      bannerDomElement.setAttribute('onview-beacon', viewBeacon);
    }

    addMetrics(bannerDomElement, requestConfig, adServer, invScriptTime, impressionBeaconAddTime);

    // Wait until the iframe content is loaded to add the listeners
    setTimeout(() => {
      const skyscraperAnchorTags = getIframeAnchorTags('skyscraper');
      onClickMetricsHandler('skyscraper', skyscraperAnchorTags, {
        mcvisID: requestConfig.mcvisID,
        conversionBeacon: data.banner.clickConversionBeacon
      });

      const sponsoredBannerAnchorTags = getIframeAnchorTags('sponsored__banner');
      onClickMetricsHandler('sponsored__banner', sponsoredBannerAnchorTags, {
        mcvisID: requestConfig.mcvisID,
        conversionBeacon: data.banner.clickConversionBeacon
      });

      const standardAnchorTags = getIframeAnchorTags('standard');
      onClickMetricsHandler('standard', standardAnchorTags, {
        mcvisID: requestConfig.mcvisID,
        conversionBeacon: data.banner.clickConversionBeacon
      });

      const standardHPAuthTags = getIframeAnchorTags('auth__banner');
      onClickMetricsHandler('auth__banner', standardHPAuthTags, {
        mcvisID: requestConfig.mcvisID,
        conversionBeacon: data.banner.clickConversionBeacon
      });

      const pipAnchorTags = getIframeAnchorTags('pip');
      onClickMetricsHandler('middle', pipAnchorTags, {
        mcvisID: requestConfig.mcvisID,
        conversionBeacon: data.banner.clickConversionBeacon
      });

      const ingridAnchorTags = getIframeAnchorTags('ingrid');
      onClickMetricsHandler('ingrid', ingridAnchorTags, {
        mcvisID: requestConfig.mcvisID,
        conversionBeacon: data.banner.clickConversionBeacon
      });

      const leaderboardAnchorTags = getIframeAnchorTags('leaderboard');
      onClickMetricsHandler('leaderboard', leaderboardAnchorTags, {
        mcvisID: requestConfig.mcvisID,
        conversionBeacon: data.banner.clickConversionBeacon
      });

      const heroAnchorTags = getIframeAnchorTags('hero');
      onClickMetricsHandler('hero', heroAnchorTags);
    }, 500);

  }
}

let heroTagRef;

function renderFrame({ data, requestConfig, invScriptTime }) {
  if (data) {
    const frameConfig = getFrameConfig({ data, requestConfig });
    // if we dont have frameConfig we dont have all the data to render revjet
    if (frameConfig) {
      frameConfig._opts.onload = () => frameLoad({ frameConfig, data, requestConfig, invScriptTime });
      if (requestConfig.bannerType === 'hero') {
        heroTagRef = window.REVJET_Tag(frameConfig);
      } else {
        window.REVJET_Tag(frameConfig);
      }
    }

  }
}

function injectRevJetScript({ data, requestConfig, adType }) {
  let invScriptTime = '';
  const revJetScript = document.createElement('script');
  revJetScript.onerror = () => onTriggerNewRelicAdBlocked(adType);
  revJetScript.onload = () => renderFrame({ data, requestConfig, invScriptTime });
  revJetScript.async = true;
  revJetScript.src = 'https://rma.homedepot.com/bg';

  const firstScript = document.getElementsByTagName('body')[0];
  firstScript.parentNode.insertBefore(revJetScript, firstScript);

  // INNERVATE SCRIPT INJECTION START TIME
  const innervateScriptAddedTime = new Date().getTime();
  invScriptTime = innervateScriptAddedTime;
}

function buildRevJetBanner({ data, requestConfig, adType }) {
  if (window.REVJET_Tag) {
    const invScriptTime = new Date().getTime();
    renderFrame({ data, requestConfig, invScriptTime });
  } else {
    injectRevJetScript({ data, requestConfig, adType });
  }
}

const dispatchMouseEvent = (windowObject, type, containerX, containerY) => {
  windowObject.document
    .elementFromPoint(containerX, containerY)
    .dispatchEvent(new windowObject.MouseEvent(type, {
      view: windowObject,
      bubbles: true,
      cancelable: true,
      clientX: containerX,
      clientY: containerY
    }));
};

/* istanbul ignore next */
const onAdTagContainerClick = (event) => {
  const rect = document.querySelector('#hp_sponsored__hero')
    .getBoundingClientRect();
  // Calculate click coordinates relative to the creative's container
  const containerX = event.clientX - rect.x;
  const containerY = event.clientY - rect.y;

  // Find the creative's iframe
  const frames = heroTagRef.frames();
  const contentWindow = frames[0].contentWindow;

  contentWindow.Pandora = {
    /**
       * If defined, will be called with correct landing page URL.
       */
    openLandingPage: (opts) => {
      // Open the landing page URL
      window.open(opts.pageURL, '_top');
    }
  };

  // In order to correctly build the creative's heatmap it is necessary here to
  // dispatch a 'mousedown' event before dispatching the 'click' event.
  dispatchMouseEvent(contentWindow, 'mousedown', containerX, containerY);
  // Dispatch the 'click' event to the creative.
  dispatchMouseEvent(contentWindow, 'click', containerX, containerY);
};

export {
  buildRevJetBanner, getFrameConfig, frameLoad, onAdTagContainerClick, renderFrame, getPubRef, injectRevJetScript, getCreativeId
};
